import { featuredWorkoutsLeadIn } from './featured-workouts-lead-in'
import { FeaturedWorkoutCard } from './featured-workout-card'
import { WorkoutCardSkeleton } from '../ui/workout-card/workout-card-skeleton'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FeaturedWorkout } from 'src/dao/core-dao'
import { useFeaturedShelfViewed } from 'src/hook/use-featured-shelf-viewed'
import { useFeaturedWorkouts } from 'src/hook/use-featured-workouts'
import { MixpanelEvent, mixpanelService } from 'src/service/mixpanel-service'

/** Wide enough that the next card peeks past the edge — the cue that tells a thumb to swipe. */
const CARD_WIDTH = 'w-[78%] max-w-[320px]'

/**
 * Skeletons shown while the set loads. Three matches the usual published set, so the rail
 * reserves the width a thumb will actually swipe through; only the first and a sliver of the
 * second are on screen, and the rest cost nothing but an empty box.
 */
const SKELETON_COUNT = 3

/**
 * The free workouts on a phone: a horizontal swipe rail, sitting between the logo and the
 * sign-in buttons.
 *
 * **Why a rail, and why up there.** Below `lg` the hero panel does not render, and the sign-in
 * column is `min-h-screen` — so a shelf placed after the form starts exactly one full screen
 * down, and nobody scrolls a login page. Putting it in the first screen is the whole point.
 * Above the buttons rather than below them so the primary action keeps its position; the email
 * step's padding drops to `p-6` under `sm` to pay for the space (the OTP step already did).
 *
 * **Why not the vertical shelf.** `FeaturedWorkoutsSection` stacks 16:9 cards one per row —
 * three of them is ~600px of column, which is the problem restated rather than solved. One
 * card at a time with the next one peeking is what a phone can actually show.
 *
 * **No preview here, deliberately.** A touch device has no hover state, so the spec gives it
 * no preview at all: a tap opens the workout. That means the rail fetches no video, which is
 * also why it is cheap to put on the busiest public page in the app.
 *
 * **Renders nothing for an empty set** — no heading, no skeleton. On a sign-in page the form is
 * the point, and a placeholder where workouts should never arrive is worse than no rail.
 *
 * **Loading is not that case.** It renders the rail's own shape in skeletons, so a phone shows
 * something is coming rather than a gap that fills in late. ⚠️ The trade-off, accepted
 * deliberately: because an empty set renders nothing, a set that loads and turns out empty
 * reserves this height and then gives it back, nudging the sign-in buttons up once. That is the
 * abnormal path — the feature being off or the public route failing — and it is worth paying for
 * a loading state on the normal one.
 */
export const FeaturedWorkoutsRail = (): React.ReactElement => {
  const { featuredWorkouts, isLoading } = useFeaturedWorkouts()
  const navigate = useNavigate()
  useFeaturedShelfViewed({ surface: 'sign-in', workoutCount: featuredWorkouts.length })

  if (!isLoading && featuredWorkouts.length === 0) return <></>

  const play = (workout: FeaturedWorkout): void => {
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_PLAY_CLICKED, {
      workoutId: workout.id,
      workoutTitle: workout.title,
      surface: 'sign-in',
      signedIn: false,
    })
    navigate(`/auth/featured/${workout.id}`)
  }

  return (
    // `max-w-[541px] mx-auto` mirrors the sign-in form's own width, so the rail lines up with
    // the fields beneath it at every sub-`lg` width — without it, a tablet gets a rail spanning
    // 800px above a 541px centred form.
    <div data-testid="featured-workouts-rail" className="w-full max-w-[541px] mx-auto flex flex-col gap-2">
      <p className="font-roboto-condensed text-base leading-6 text-white tracking-[0.5px] m-0">{featuredWorkoutsLeadIn()}</p>
      {/* Scroll snapping so a swipe lands on a card rather than between two. The scrollbar is
          hidden because on a touch device it is chrome over the poster, and the peeking card
          already says there is more to the right. */}
      <div
        data-testid="featured-workouts-rail-track"
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {isLoading
          ? // Same width and the same default `aspect-[16/9]` the loaded cards resolve to, so
            // the rail's height does not change when the real cards arrive.
            Array.from({ length: SKELETON_COUNT }, (_, index) => (
              <div key={`featured-rail-skeleton-${index}`} className={`shrink-0 snap-start ${CARD_WIDTH}`}>
                <WorkoutCardSkeleton />
              </div>
            ))
          : // Rendered in the order the server gave them — shortest to longest. Do not sort.
            featuredWorkouts.map((workout) => (
              // No `fill`: the card's own `aspect-[16/9]` is exactly right once width is fixed,
              // so the rail sets width only and lets the card decide its height.
              <div key={workout.id} className={`shrink-0 snap-start ${CARD_WIDTH}`}>
                {/* `compact`: at 78% of a 320px screen the card is 212x119, and the card
                    system's fixed 36px title over a 30px meta row needs ~250px of width to fit
                    a 16:9 box. Without it the trainer name and the duration badge are clipped
                    off the bottom — see the prop's own note. */}
                <FeaturedWorkoutCard workout={workout} compact surface="sign-in" onAction={() => play(workout)} />
              </div>
            ))}
      </div>
    </div>
  )
}
